/**
 * Outlets Page
 * Page for managing outlets (locations/branches)
 */
import { Button, ReadOnlyAccessNotice } from '@/renderer/components/common';
import { Modal } from '@/renderer/components/common/Modal';
import {
  DeleteOutletContent,
  OutletCard,
  OutletForm,
} from '@/renderer/components/outlets';
import { AppDispatch } from '@/renderer/store';
import {
  selectCanManageOutlets,
  selectCurrentUser,
} from '@/renderer/store/auth/authSelectors';
import {
  createOutlet,
  deleteOutlet,
  fetchOutletLimits,
  fetchOutletsWithStatsThunk,
  selectAllOutlets,
  selectOutletError,
  selectOutletLimits,
  selectOutletLoading,
  updateOutlet,
} from '@/renderer/store/outlets';
import {
  CreateOutletRequest,
  OutletFormValues,
  OutletWithStats,
  UpdateOutletRequest,
} from '@/renderer/types/outlet';
import { BusinessType, SubscriptionPackage } from '@/shared/types';
import { AlertCircle, ArrowUpRight, Building2, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BILLING_PAGE_PATH } from '../../util/renderConstants';
import { useOnboardingDriverTour } from '../../hooks/useOnboardingDriverTour';
import {
  createTourElementResolver,
  ONBOARDING_TOUR_TARGETS,
  onboardingTourSelector,
} from '../../services/onboardingTourService';

const OutletsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector(selectCurrentUser);
  const outlets = useSelector(selectAllOutlets);
  const limits = useSelector(selectOutletLimits);
  const isLoading = useSelector(selectOutletLoading);
  const error = useSelector(selectOutletError);
  const canManageOutlets = useSelector(selectCanManageOutlets);
  const navigate = useNavigate();

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState<OutletWithStats | null>(
    null,
  );
  const [deletingOutlet, setDeletingOutlet] = useState<OutletWithStats | null>(
    null,
  );

  const onboardingTourSteps = useMemo(
    () => [
      {
        element: onboardingTourSelector(ONBOARDING_TOUR_TARGETS.outletsPage),
        popover: {
          title: 'Review your outlets',
          description:
            'This page is where you review the outlet created during signup, update its details, or add more branches later.',
          side: 'bottom' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.outletsAddButton),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.outletsEmptyAddButton),
        ]),
        popover: {
          title: 'Add another outlet',
          description:
            'Use this button whenever you want to add a new branch or location to the company.',
          side: 'left' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(
            ONBOARDING_TOUR_TARGETS.primaryOutletEditButton,
          ),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.primaryOutletCard),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.outletsGrid),
        ]),
        popover: {
          title: 'Edit the outlet you already created',
          description:
            'Use the existing outlet card to update the outlet name, address, and other details created during onboarding.',
          side: 'left' as const,
        },
      },
    ],
    [],
  );

  useOnboardingDriverTour({
    stepId: 'outlets',
    steps: onboardingTourSteps,
  });

  useEffect(() => {
    if (currentUser?.company?.id) {
      dispatch(fetchOutletsWithStatsThunk(currentUser.company.id));
      dispatch(
        fetchOutletLimits({
          companyId: currentUser.company.id,
          packageType:
            (currentUser.subscription?.package as SubscriptionPackage) ||
            'starter',
        }),
      );
    }
  }, [dispatch, currentUser?.company?.id, currentUser?.subscription?.package]);

  const handleCreateClick = () => {
    if (!canManageOutlets) return;
    setEditingOutlet(null);
    setIsFormModalOpen(true);
  };

  const handleEditClick = (outlet: OutletWithStats) => {
    if (!canManageOutlets) return;
    setEditingOutlet(outlet);
    setIsFormModalOpen(true);
  };

  const handleDeleteClick = (outlet: OutletWithStats) => {
    if (!canManageOutlets) return;
    setDeletingOutlet(outlet);
  };

  const handleFormSubmit = async (data: OutletFormValues) => {
    if (!currentUser?.company?.id || !canManageOutlets) return;

    if (editingOutlet) {
      const updateRequest: UpdateOutletRequest = {
        id: editingOutlet.id,
        name: data.name,
        address: data.address || undefined,
        tin_number: data.tinNumber || undefined,
        phone: data.phone || undefined,
        receipt_footnote: data.receiptFootnote || undefined,
        business_type: data.businessType,
        kot_tracking_enabled: data.kotTrackingEnabled,
        is_kds_enabled: data.isKdsEnabled,
      };
      await dispatch(updateOutlet(updateRequest));
    } else {
      const createRequest: CreateOutletRequest = {
        company_id: currentUser.company.id,
        name: data.name,
        address: data.address || undefined,
        tin_number: data.tinNumber || undefined,
        phone: data.phone || undefined,
        receipt_footnote: data.receiptFootnote || undefined,
        business_type: data.businessType,
        kot_tracking_enabled: data.kotTrackingEnabled,
        is_kds_enabled: data.isKdsEnabled,
      };
      await dispatch(
        createOutlet({
          request: createRequest,
          packageType:
            (currentUser.subscription?.package as SubscriptionPackage) ||
            'starter',
        }),
      );
    }

    setIsFormModalOpen(false);
    setEditingOutlet(null);
    dispatch(fetchOutletsWithStatsThunk(currentUser.company.id));
  };

  const handleDeleteConfirm = async () => {
    if (deletingOutlet && canManageOutlets) {
      await dispatch(deleteOutlet(deletingOutlet.id));
      setDeletingOutlet(null);
    }
  };

  const canCreateOutlet = limits?.canCreateOutlet ?? true;

  if (!currentUser?.outlet_id) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-500">
          <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
          <p>No outlet selected. Please select an outlet first.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="max-w-5xl mx-auto p-6 space-y-6"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.outletsPage}
    >
      {!canManageOutlets ? (
        <ReadOnlyAccessNotice message="You can view outlet details here, but only authorized roles can create, update, or delete outlets." />
      ) : null}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Outlets</h1>
          <p className="text-gray-500 mt-1">
            Manage your business locations and branches
          </p>
        </div>
        <div className="flex items-center gap-4">
          {limits && (
            <span className="text-sm text-gray-500">
              {limits.currentOutlets} /{' '}
              {limits.maxOutlets === Infinity ? '∞' : limits.maxOutlets} outlets
            </span>
          )}
          {canManageOutlets ? (
            <Button
              onClick={handleCreateClick}
              disabled={!canCreateOutlet || isLoading}
              data-onboarding-tour={ONBOARDING_TOUR_TARGETS.outletsAddButton}
            >
              <Plus size={18} />
              Add Outlet
            </Button>
          ) : null}
        </div>
      </div>

      {/* Limit Warning */}
      {!canCreateOutlet && limits && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle size={18} className="text-amber-500" />
          <span className="text-amber-700">
            You&apos;ve reached the outlet limit for your plan (
            {limits.maxOutlets} outlets).
          </span>
          <button
            type="button"
            onClick={() => navigate(BILLING_PAGE_PATH)}
            className="text-amber-700 font-medium hover:underline flex items-center gap-1 ml-auto"
          >
            Upgrade to add more
            <ArrowUpRight size={14} />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle size={18} className="text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && outlets.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      ) : outlets.length === 0 ? (
        /* Empty State */
        <div className="text-center py-16">
          <Building2 size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No outlets yet
          </h2>
          <p className="text-gray-500 mb-6">
            {canManageOutlets
              ? 'Create your first outlet to start managing your business locations.'
              : 'Outlets created for this company will appear here once an authorized user adds them.'}
          </p>
          {canManageOutlets ? (
            <button
              type="button"
              onClick={handleCreateClick}
              data-onboarding-tour={
                ONBOARDING_TOUR_TARGETS.outletsEmptyAddButton
              }
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              Create First Outlet
            </button>
          ) : null}
        </div>
      ) : (
        /* Outlets Grid */
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          data-onboarding-tour={ONBOARDING_TOUR_TARGETS.outletsGrid}
        >
          {outlets.map((outlet, index) => (
            <OutletCard
              key={outlet.id}
              canManage={canManageOutlets}
              isTourPrimary={index === 0}
              outlet={outlet as OutletWithStats}
              isCurrentOutlet={outlet.id === currentUser?.outlet?.id}
              onEdit={() => handleEditClick(outlet as OutletWithStats)}
              onDelete={() => handleDeleteClick(outlet as OutletWithStats)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingOutlet(null);
        }}
        title={editingOutlet ? 'Edit Outlet' : 'Create New Outlet'}
        size="md"
      >
        <OutletForm
          initialName={editingOutlet?.name || ''}
          initialAddress={editingOutlet?.address || ''}
          initialTinNumber={editingOutlet?.tin_number || ''}
          initialPhone={editingOutlet?.phone || ''}
          initialReceiptFootnote={editingOutlet?.receipt_footnote || ''}
          initialBusinessType={
            editingOutlet?.business_type ||
            currentUser?.company?.business_type ||
            BusinessType.OTHER
          }
          initialKotTrackingEnabled={
            editingOutlet?.kot_tracking_enabled ?? false
          }
          initialIsKdsEnabled={editingOutlet?.is_kds_enabled ?? false}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsFormModalOpen(false);
            setEditingOutlet(null);
          }}
          isLoading={isLoading}
          isEditing={!!editingOutlet}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingOutlet}
        onClose={() => setDeletingOutlet(null)}
        title="Delete Outlet"
        size="sm"
      >
        <DeleteOutletContent
          outletName={deletingOutlet?.name || ''}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingOutlet(null)}
          isLoading={isLoading}
        />
      </Modal>
    </div>
  );
};

export default OutletsPage;
